import { Injectable, NgZone } from '@angular/core';
import { OAuthAccount, OAuthTransactionService } from './oauth-transaction.service';

/**
 * Layer 9A.1 — owns the popup mechanics for **redirect-based** OAuth
 * (Razorpay today; Calendly / Zoho / future networks pluggable).
 *
 * Sibling to `MetaOAuthLaunchService`. Same workspace-facing
 * contract (synchronous-from-click + discriminated result), with a 15-minute
 * authorization window and different popup mechanics underneath:
 *
 *   - Meta: `FB.login()` callback inside the SAME window
 *   - Redirect: `window.open(oauthUrl)` opens a new window; provider
 *     redirects to `/oauth/<id>?code=...` INSIDE that window;
 *     `AuthRedirectComponent` (popup-mode) submits CREATE_CONNECTION
 *     and posts the result back via `postMessage`; opener
 *     (this service) listens for it.
 *
 * **Popup-blocker constraint** (load-bearing, same as Meta):
 * `window.open(...)` MUST be invoked synchronously inside the
 * user-click handler. No `await` between the click and `window.open`.
 * The promise this service returns resolves AFTER postMessage or
 * watchdog — by then the popup is already open, gesture activation
 * no longer matters.
 *
 * **Why the popup runs the submission, not us**: the OAuth code
 * arrives via a redirect into the popup window, not via a JS
 * callback into the opener. AuthRedirectComponent already exists in
 * that window with the code in its query params and an
 * `IntegrationActions(CREATE_CONNECTION)` call ready to fire.
 * Reaching back into the opener to do the submission would
 * duplicate code and complicate state. So the popup self-contains:
 * code → CREATE_CONNECTION → postMessage(result) → close.
 *
 * **PostMessage origin discipline**: listener accepts ONLY messages
 * where ALL of these match:
 *   1. `event.source === popupRef` — the message came from the
 *      popup we opened, not some other window/iframe on the page
 *   2. `event.origin === window.location.origin` — same-origin only
 *   3. `event.data?.source === 'salesmax-oauth'` — sentinel
 *   4. `event.data?.integrationId === networkType` — cross-resolve
 *      guard (rep raced two OAuth flows)
 *   5. `event.data?.state === launch.transaction.state` — this attempt
 * Without `(1)` a stale same-origin window could spoof a result.
 *
 * **Environment-origin caveat (load-bearing for smoke)**: OAuth
 * redirects use the app domain selected by the Angular environment.
 * A localhost build may point at a deployed callback domain. Launch
 * validation rejects that mismatch before opening the provider and
 * asks the rep to use the matching SalesMax site. Any foreign-origin
 * result that nevertheless arrives is ignored.
 */
@Injectable({ providedIn: 'root' })
export class RedirectOauthLaunchService {
  /**
   * Match the transaction's lifetime so password/2FA/consent flows are not
   * interrupted at 90 seconds. Timeout stops the opener's listener; callback
   * validation still enforces the record's expiry.
   */
  private static readonly LAUNCH_WATCHDOG_MS = 15 * 60_000;

  /** Sentinel so we ignore unrelated postMessage traffic. */
  private static readonly MESSAGE_SOURCE = 'salesmax-oauth';

  constructor(private ngZone: NgZone, private transactions: OAuthTransactionService) {}

  /**
   * **MUST be called synchronously inside a user-click handler.**
   *
   * Caller supplies the OAuth URL already constructed (typically
   * pulled from `IntegrationsComponent.allRedirectUrls[networkType]`
   * — see the parent's `getAllRedirectUrls()` for how the map is
   * populated). We deliberately do NOT fetch the URL here so this
   * service stays free of API dependencies and can be unit-tested
   * with no network mocks.
   *
   * `networkType` is matched against incoming postMessage payloads
   * to ignore noise (a postMessage for FB shouldn't resolve a
   * Razorpay launch).
   */
  launchRedirect(networkType: string, oauthUrl: string, account: OAuthAccount): Promise<RedirectOauthResult> {
    if (!oauthUrl) {
      return Promise.resolve({ ok: false, reason: 'url-missing' });
    }
    let launch: ReturnType<OAuthTransactionService['prepare']>;
    try { launch = this.transactions.prepare(networkType, oauthUrl, account); }
    catch (error) {
      return Promise.resolve({ ok: false, reason: 'launch-invalid',
        message: error instanceof Error ? error.message : 'Could not start authorization securely. Check your browser storage settings and try again.' });
    }
    return new Promise<RedirectOauthResult>((resolve) => {
      let settled = false;
      let popupRef: Window | null = null;

      const settle = (r: RedirectOauthResult, cancelLaunch = false) => {
        if (settled) return;
        settled = true;
        clearTimeout(watchdog);
        clearInterval(closedPoll);
        window.removeEventListener('message', onMessage);
        // A closed WindowProxy can also mean the provider severed its opener.
        // Keep the record until consumption/expiry in that case; otherwise a
        // valid return in the still-open provider tab would be rejected.
        if (cancelLaunch) this.transactions.cancel(launch.transaction.state);
        resolve(r);
      };

      const onMessage = (event: MessageEvent) => {
        // Strict source-window validation — only accept messages
        // from the popup we opened, not from other same-origin
        // windows or iframes.
        if (popupRef && event.source !== popupRef) return;
        // Origin validation. Mismatch is usually the env caveat in
        // the docstring (workspace on dev/staging/localhost, popup
        // landed on app.salesmax.ai); log it once to aid diagnosis.
        if (event.origin !== window.location.origin) {
          // Only warn when the message LOOKS like one of ours —
          // avoids logging every cross-origin postMessage on the
          // page (analytics, ads, etc.).
          const data: any = event.data;
          if (data && data.source === RedirectOauthLaunchService.MESSAGE_SOURCE) {
            console.warn(
              `[RedirectOauthLaunchService] dropped salesmax-oauth message from foreign origin "${event.origin}". `
              + `Workspace origin is "${window.location.origin}". `
              + `Common cause: legacy OAuth redirect_uri is hardcoded to a fixed environment (e.g. app.salesmax.ai); `
              + `redirect-popup OAuth must be tested on the same env the redirect_uri points at.`,
            );
          }
          return;
        }
        const data: any = event.data;
        if (!data || data.source !== RedirectOauthLaunchService.MESSAGE_SOURCE) return;
        // Filter by networkType so a stale message from a different
        // provider's popup (rep raced two flows) doesn't cross-resolve.
        if (data.integrationId !== networkType || data.state !== launch.transaction.state) return;
        this.ngZone.run(() => {
          if (data.ok === true) {
            settle({ ok: true, networkType });
          } else {
            settle({
              ok: false,
              reason: 'submit-failed',
              message: typeof data.message === 'string' ? data.message : undefined,
            });
          }
        });
      };
      window.addEventListener('message', onMessage);

      // Watchdog — see class docstring for rationale.
      const watchdog = setTimeout(() => {
        settle({ ok: false, reason: 'submit-failed', message: 'Authorization timed out. Start the connection again.' });
      }, RedirectOauthLaunchService.LAUNCH_WATCHDOG_MS);

      // Periodic check for popup closed without sending a message
      // (rep dismissed the OAuth page mid-flow). Polling because the
      // `onclose` event is unreliable cross-origin.
      const closedPoll = setInterval(() => {
        if (popupRef && popupRef.closed) {
          settle({ ok: false, reason: 'user-cancelled' });
        }
      }, 500);

      try {
        // Synchronous invocation inside the click task. The launch nonce
        // gives each attempt its own window and message correlation.
        const windowName = `salesmax-oauth-${networkType.toLowerCase()}-${launch.transaction.state}`;
        popupRef = window.open(
          launch.url,
          windowName,
          'width=520,height=720,menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes',
        );
        if (!popupRef) {
          // window.open returned null — browser blocked the popup
          // outright (no permission). Resolve immediately so the
          // primitive can surface "popup-blocked" copy.
          settle({ ok: false, reason: 'popup-blocked' }, true);
          return;
        }
        // Try to focus the popup — best-effort; some browsers
        // ignore this when the gesture is consumed by the open call.
        try { popupRef.focus(); } catch { /* noop */ }
      } catch (err) {
        // window.open threw — extremely unusual; treat as blocked.
        settle({ ok: false, reason: 'popup-blocked', raw: err }, true);
      }
    });
  }
}

/**
 * Discriminated result. Mirrors `MetaOAuthResult`'s shape closely
 * but with redirect-specific failure reasons:
 *   - `url-missing`     — caller didn't supply an OAuth URL
 *                         (typically backend's GET_CONNECTION_URL
 *                         doesn't ship this network yet → Layer 10
 *                         backend ask)
 *   - `popup-blocked`   — browser blocked the popup
 *   - `user-cancelled`  — popup closed without a postMessage
 *                         (rep dismissed mid-OAuth)
 *   - `submit-failed`   — popup posted ok:false (CREATE_CONNECTION
 *                         on the backend rejected the code or the
 *                         API errored)
 *
 * Success carries `networkType` for symmetry with the Meta result —
 * lets the workspace dispatch by provider without having to thread
 * extra context.
 */
export type RedirectOauthResult =
  | { ok: true; networkType: string }
  | {
      ok: false;
      reason: 'url-missing' | 'popup-blocked' | 'user-cancelled' | 'submit-failed' | 'launch-invalid';
      message?: string;
      raw?: unknown;
    };
